import type { DeviceSession } from '@mfos/shared';
import { useState } from 'react';
import { Badge } from '../../components/Badge';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorNotice, LoadingNotice, SuccessNotice } from '../../components/ErrorNotice';
import { formatDateTime, humanize } from '../../lib/format';
import {
  useDeviceSessions,
  useRevokeOtherSessions,
  useRevokeSession,
  useUserTimeZone,
} from '../auth/auth.api';
import { phrase } from '../../lib/i18n/runtime';

export function DeviceSessionsSection() {
  const sessions = useDeviceSessions();
  const timeZone = useUserTimeZone();
  const revoke = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();
  const [target, setTarget] = useState<DeviceSession | null>(null);
  const [confirmOthers, setConfirmOthers] = useState(false);

  const items = sessions.data?.items ?? [];
  const others = items.filter((item) => !item.current);

  return (
    <section className="card stack" aria-labelledby="sessions-heading">
      <h2 id="sessions-heading">{phrase('Signed-in devices')}</h2>
      {sessions.isPending && <LoadingNotice>{phrase('Loading sessions…')}</LoadingNotice>}
      {sessions.isError && <ErrorNotice error={sessions.error} />}
      {revoke.isSuccess && <SuccessNotice>{phrase('The session was signed out.')}</SuccessNotice>}
      {revokeOthers.isSuccess && (
        <SuccessNotice>
          Signed out {revokeOthers.data.revoked} other session
          {revokeOthers.data.revoked === 1 ? '' : 's'}.
        </SuccessNotice>
      )}
      {sessions.isSuccess && (
        <div
          className="table-scroll"
          role="region"
          aria-label={phrase('Signed-in devices')}
          tabIndex={0}
        >
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{phrase('Device')}</th>
                <th scope="col">{phrase('Signed in with')}</th>
                <th scope="col">{phrase('IP address')}</th>
                <th scope="col">{phrase('Started')}</th>
                <th scope="col">{phrase('Last active')}</th>
                <th scope="col">{phrase('Two-step')}</th>
                <th scope="col">
                  <span className="visually-hidden">{phrase('Actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((session) => (
                <tr key={session.id}>
                  <th scope="row">
                    {session.userAgent ?? phrase('Unknown device')}
                    {session.current && (
                      <>
                        {' '}
                        <Badge tone="ok" label={phrase('This device')} />
                      </>
                    )}
                  </th>
                  <td>{humanize(session.authMethod)}</td>
                  <td>{session.ipAddress ?? '—'}</td>
                  <td>{formatDateTime(session.createdAt, timeZone)}</td>
                  <td>
                    {session.lastUsedAt !== null
                      ? formatDateTime(session.lastUsedAt, timeZone)
                      : '—'}
                  </td>
                  <td>{session.mfaVerified ? phrase('Verified') : phrase('Not verified')}</td>
                  <td>
                    {!session.current && (
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        onClick={() => {
                          revoke.reset();
                          setTarget(session);
                        }}
                      >
                        Sign out{' '}
                        <span className="visually-hidden">
                          {session.userAgent ?? 'unknown device'}
                        </span>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {others.length > 0 && (
        <div className="form-actions">
          <button
            type="button"
            className="button button--danger"
            onClick={() => {
              revokeOthers.reset();
              setConfirmOthers(true);
            }}
          >
            {phrase('Sign out all other sessions')}
          </button>
        </div>
      )}

      <ConfirmDialog
        open={target !== null}
        title={phrase('Sign out this session?')}
        description={`${target?.userAgent ?? phrase('Unknown device')} will need to sign in again.`}
        confirmLabel={phrase('Sign out session')}
        tone="danger"
        isPending={revoke.isPending}
        error={revoke.error}
        onCancel={() => {
          setTarget(null);
        }}
        onConfirm={() => {
          if (target !== null) {
            revoke.mutate(target.id, {
              onSuccess: () => {
                setTarget(null);
              },
            });
          }
        }}
      />
      <ConfirmDialog
        open={confirmOthers}
        title={phrase('Sign out all other sessions?')}
        description={`${String(others.length)} other ${others.length === 1 ? 'session' : 'sessions'} will need to sign in again. This device stays signed in.`}
        confirmLabel={phrase('Sign out others')}
        tone="danger"
        isPending={revokeOthers.isPending}
        error={revokeOthers.error}
        onCancel={() => {
          setConfirmOthers(false);
        }}
        onConfirm={() => {
          revokeOthers.mutate(undefined, {
            onSuccess: () => {
              setConfirmOthers(false);
            },
          });
        }}
      />
    </section>
  );
}
